import { useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  FormHelperText,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography
} from '@mui/material';
import BugReportOutlinedIcon from '@mui/icons-material/BugReportOutlined';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import { toast } from 'react-toastify';
import AppDialogTitle from '../ui/appDialogTitle';
import { FeedbackService } from '../../service/feedbackService';

const MAX_TITLE_LENGTH = 100;
const MAX_DETAILS_LENGTH = 1500;

export default function FeedbackDialog({ open, onClose, locationId = null }) {
  const [type, setType] = useState('bug');
  const [title, setTitle] = useState('');
  const [details, setDetails] = useState('');
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const validate = () => {
    const nextErrors = {};
    if (!title.trim()) nextErrors.title = 'Title is required';
    if (!details.trim()) nextErrors.details = 'Details are required';
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate() || submitting) return;

    setSubmitting(true);
    try {
      await FeedbackService.submit({
        type,
        title: title.trim(),
        details: details.trim(),
        pagePath: `${window.location.pathname}${window.location.search}`,
        locationId
      });
      toast.success('Thank you. Your feedback was sent.');
      onClose();
    } catch (error) {
      // The shared HTTP interceptor presents the server error.
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} fullWidth maxWidth="sm">
      <AppDialogTitle title="Send feedback" onClose={submitting ? undefined : onClose} />
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Tell us what happened or what would make OptoDesk work better for you.
          </Typography>

          <div>
            <ToggleButtonGroup
              value={type}
              exclusive
              fullWidth
              size="small"
              aria-label="Feedback type"
              onChange={(event, value) => value && setType(value)}
            >
              <ToggleButton value="bug" aria-label="Bug">
                <BugReportOutlinedIcon fontSize="small" sx={{ mr: 1 }} />
                Bug
              </ToggleButton>
              <ToggleButton value="feature" aria-label="Feature request">
                <LightbulbOutlinedIcon fontSize="small" sx={{ mr: 1 }} />
                Feature request
              </ToggleButton>
            </ToggleButtonGroup>
            <FormHelperText>Choose the option that best matches your feedback.</FormHelperText>
          </div>

          <TextField
            label="Title"
            value={title}
            onChange={(event) => {
              setTitle(event.target.value);
              if (errors.title) setErrors(current => ({ ...current, title: null }));
            }}
            inputProps={{ maxLength: MAX_TITLE_LENGTH }}
            error={Boolean(errors.title)}
            helperText={errors.title || `${title.length}/${MAX_TITLE_LENGTH}`}
            autoFocus
            fullWidth
          />

          <TextField
            label="Details"
            value={details}
            onChange={(event) => {
              setDetails(event.target.value);
              if (errors.details) setErrors(current => ({ ...current, details: null }));
            }}
            inputProps={{ maxLength: MAX_DETAILS_LENGTH }}
            error={Boolean(errors.details)}
            helperText={errors.details || `${details.length}/${MAX_DETAILS_LENGTH}`}
            placeholder={type === 'bug'
              ? 'What were you doing, what did you expect, and what happened instead?'
              : 'What would you like to do, and how would it help your workflow?'}
            minRows={6}
            multiline
            fullWidth
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={submitting}>
          {submitting ? 'Sending...' : 'Send feedback'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}